import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useEffect, useRef, useState, useCallback } from "react";
import UserService from "../UserService";

const INITIAL_UPLOAD_PROGRESS = { Current: 0, Total: 0 };

export function useMessageConsumer(vhost) {
    const connectionRef = useRef(null);
    const uploadActiveRef = useRef(false);
    const uploadProgressRef = useRef(0);
    const uploadErrorCountRef = useRef(0);
    const [isConnected, setIsConnected] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [responseMessage, setResponseMessage] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(INITIAL_UPLOAD_PROGRESS);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [uploadCompleted, setUploadCompleted] = useState(null);

    const invoke = useCallback(async (method, ...args) => {
        try {
            const result = await connectionRef.current?.invoke(method, ...args);
            return result;
        } catch (err) {
            setError(err.toString());
            throw err;
        }
    }, []);

    const invokeAction = useCallback(async (method, ...args) => {
        setError(null);
        setResponseMessage(null);
        return invoke(method, ...args);
    }, [invoke]);

    useEffect(() => {
        if (!vhost) return;

        // remove the /SD from the api url
        let baseURL = process.env.REACT_APP_API_URL;
        let signalRUrl = baseURL + `hubs/message-controller?vhost=${vhost}`;

        const connection = new HubConnectionBuilder()
            .withUrl(signalRUrl,{
                accessTokenFactory: async () => {
                    await UserService.updateToken(() => Promise.resolve());
                    return UserService.getToken();
                }
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        connection.on("MessageReceived", (msg) => {
            setMessage(msg);
        });
        connection.on("AckSuccess", (ackMessage) => {
            setMessage(null);
            setError(null);
            setResponseMessage(ackMessage ?? null);
        });
        connection.on("RequeueSuccess", (requeueMessage) => {
            setMessage(null);
            setError(null);
            setResponseMessage(requeueMessage ?? null);
        });
        connection.on("MoveSuccess", (moveMessage) => {
            setMessage(null);
            setError(null);
            setResponseMessage(moveMessage ?? null);
        });
        connection.on("MessageReleased", (err) => {
            setMessage(null);
            setError(err);
        });
        connection.on("Progress", (progress) => {
            uploadProgressRef.current = progress.Current;
            setUploadProgress(progress);
        });
        connection.on("Error", (err) => {
            setError(err);
            if (uploadActiveRef.current) {
                const failedFileNumber = uploadProgressRef.current + uploadErrorCountRef.current + 1;
                uploadErrorCountRef.current += 1;
                setUploadErrors((previousErrors) => [
                    ...previousErrors,
                    { number: failedFileNumber, error: err },
                ]);
            }
        });
        connection.on("Completed", (total) => {
            uploadActiveRef.current = false;
            setUploadCompleted(total);
        });

        connection.onreconnecting(() => setIsConnected(false));
        connection.onreconnected(() => setIsConnected(true));
        connection.onclose(() => setIsConnected(false));

        connection.start()
            .then(() => {
                setIsConnected(true);
                setError(null);
            })
            .catch((err) => setError(err.toString()));

        connectionRef.current = connection;

        return () => {
            connection.stop();
        };
    }, [vhost]);

    const startConsuming = useCallback((queueName) => {
        return invoke("StartConsuming", queueName);
    }, [invoke]);

    const ack = useCallback(() => {
        return invokeAction("Ack");
    }, [invokeAction]);

    const sendToSendmessage = useCallback(() => {
        return invokeAction("SendToSendmessage");
    }, [invokeAction]);

    const removeRetry = useCallback(() => {
        return invokeAction("RemoveRetry");
    }, [invokeAction]);

    const requeue = useCallback(() => {
        return invokeAction("Requeue");
    }, [invokeAction]);

    const moveMessage = useCallback((queueName) => {
        return invokeAction("MoveMessage", queueName);
    }, [invokeAction]);

    const filterMessages = useCallback((queueName, headers) => {
        return invoke("FilterOutMessage", queueName, headers);
    }, [invoke]);

    const deliverAllXML = useCallback(async (connectMessages, targetQueue) => {
        uploadActiveRef.current = true;
        uploadProgressRef.current = 0;
        uploadErrorCountRef.current = 0;
        setUploadProgress(INITIAL_UPLOAD_PROGRESS);
        setUploadErrors([]);
        setUploadCompleted(null);
        setError(null);
        setResponseMessage(null);

        try {
            const result = await connectionRef.current?.invoke("DeliverAllXML", connectMessages, targetQueue);
            return result;
        } catch (err) {
            uploadActiveRef.current = false;
            setError(err.toString());
            throw err;
        }
    }, []);

    const resetMessage = useCallback(() => {
        setMessage(null);
    }, []);

    return {
        isConnected,
        message,
        error,
        responseMessage,
        startConsuming,
        ack,
        sendToSendmessage,
        removeRetry,
        requeue,
        moveMessage,
        filterMessages,
        resetMessage,
        deliverAllXML,
        uploadProgress,
        uploadErrors,
        uploadCompleted,
    };
}